import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckCircleIcon } from '@phosphor-icons/react';
import toast from 'react-hot-toast';

import { SpinnerBlock } from '@/ui/Spinner';
import { Button } from '@/ui/button/Button';
import { Input } from '@/ui/Input';
import { Card } from '@/ui/Card';
import { fetchMe } from '@/api-models/user';
import { AuthLayout } from '@/features/auth/components';
import { AUTH_FEATURES, AUTH_HEADLINE } from '@/features/auth/components/authMarketing';
import { submitBetaRequest } from '@/api-models/auth-beta-request';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/Select';

const AUTH_STATS = [
  { value: 'Private', label: 'beta — invite only' },
  { value: '2.4s', label: 'avg workspace ready' },
  { value: 'AI', label: 'agents on every project' },
];

const HEARD_FROM_OPTIONS = [
  'Twitter / X',
  'LinkedIn',
  'Hacker News',
  'A friend or colleague',
  'YouTube',
  'GitHub',
  'Search engine',
  'Other',
];

const EMAIL_PATTERN = /^.+@.+\..+$/;

interface FieldErrors {
  email?: string;
  name?: string;
}

export const SignUpPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [company, setCompany] = useState('');
  const [heardFrom, setHeardFrom] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});

  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const data = await fetchMe();
        if (data.id) {
          navigate('/app');
        }
      } catch {
        // Not authenticated
      } finally {
        setIsLoading(false);
      }
    };
    checkAuthStatus();
  }, [navigate]);

  if (isLoading) {
    return <SpinnerBlock message="Loading..." className="h-screen" />;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;

    const next: FieldErrors = {};
    if (!email.trim()) {
      next.email = 'Email is required';
    } else if (!EMAIL_PATTERN.test(email.trim())) {
      next.email = 'Please enter a valid email';
    }
    if (!name.trim()) {
      next.name = 'Name is required';
    }
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setSubmitting(true);
    try {
      await submitBetaRequest({
        email: email.trim(),
        name: name.trim(),
        job_title: jobTitle.trim() || undefined,
        company: company.trim() || undefined,
        heard_from: heardFrom.trim() || undefined,
      });
      setSubmitted(true);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not submit your request — please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout
      headline={AUTH_HEADLINE}
      subline="Cloud workspaces where you and AI agents build together. Currently in private beta — request access below."
      features={AUTH_FEATURES}
      stats={AUTH_STATS}
      pageTitle="Join the beta — Zooid"
    >
      <div className="flex flex-col items-center w-full">
        {submitted ? (
          <Card
            radius="2xl"
            padding="none"
            className="w-full max-w-110 flex flex-col items-center text-center shadow-sm p-8"
          >
            <CheckCircleIcon size={48} weight="fill" className="text-success mb-4" />
            <h2 className="text-display-sm font-bold tracking-tight text-fg mb-3">You&apos;re on the list</h2>
            <p className="text-md text-fg-secondary leading-relaxed">
              Thanks for the interest! We&apos;ll review your request and email you at{' '}
              <span className="font-medium text-fg">{email}</span> when you&apos;re approved.
            </p>
            <Link to="/login" className="mt-8 text-xs font-semibold text-brand-text hover:text-brand-hover">
              Already invited? Sign in →
            </Link>
          </Card>
        ) : (
          <Card radius="2xl" padding="none" className="w-full max-w-110 shadow-sm p-6 sm:p-8">
            <div className="mb-5 flex justify-end text-xs text-fg-secondary">
              Already invited?
              <Link to="/login" className="ml-1 font-semibold text-brand-text hover:text-brand-hover">
                Sign in
              </Link>
            </div>

            <h2 className="text-[26px] font-bold tracking-tight text-fg mb-1.5">Join the private beta</h2>
            <p className="text-sm text-fg-secondary mb-6">
              Tell us a bit about yourself and we&apos;ll send you an invite when a spot opens up.
            </p>

            <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-3.5 text-left">
              <Field label="Email" required error={errors.email}>
                <Input
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (errors.email) setErrors((prev) => ({ ...prev, email: undefined }));
                  }}
                  placeholder="you@example.com"
                  error={!!errors.email}
                  aria-invalid={!!errors.email}
                />
              </Field>

              <Field label="Name" required error={errors.name}>
                <Input
                  type="text"
                  autoComplete="name"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }));
                  }}
                  placeholder="Your name"
                  error={!!errors.name}
                  aria-invalid={!!errors.name}
                />
              </Field>

              <Field label="Job title" optional>
                <Input
                  type="text"
                  autoComplete="organization-title"
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                  placeholder="e.g. Full-stack engineer"
                />
              </Field>

              <Field label="Company" optional>
                <Input
                  type="text"
                  autoComplete="organization"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder="Where you work"
                />
              </Field>

              <Field label="How did you hear about us?" optional>
                <Select value={heardFrom} onValueChange={setHeardFrom}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select an option…" />
                  </SelectTrigger>
                  <SelectContent>
                    {HEARD_FROM_OPTIONS.map((opt) => (
                      <SelectItem key={opt} value={opt}>
                        {opt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <Button type="submit" variant="primary" size={9} fullWidth disabled={submitting} isLoading={submitting}>
                Request beta access
              </Button>
            </form>

            <p className="mt-5 text-center text-2xs text-fg-muted">
              By continuing, you agree to our Terms of Service and Privacy Policy
            </p>
          </Card>
        )}
      </div>
    </AuthLayout>
  );
};

function Field({
  label,
  required,
  optional,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  optional?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="flex items-baseline gap-1.5 text-xs font-medium text-fg-secondary">
        <span>
          {label}
          {required && <span className="text-brand-text ml-0.5">*</span>}
        </span>
        {optional && <span className="text-2xs font-normal text-fg-muted">(optional)</span>}
      </span>
      {children}
      {error && <span className="text-xs text-danger">{error}</span>}
    </label>
  );
}
